import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { createStructuredSelector } from 'reselect'
import { connect, useDispatch, useSelector } from 'react-redux'
import { injectIntl } from 'react-intl'
import { array, bool, func, object } from 'prop-types'
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom'
import * as actions from './actions'
import {
  makeSelectCreatingPinField,
  makeSelectSavingProjectDetails,
  makeSelectSavingProjectDetailsError,
  makeSelectProjectDetails,
  makeSelectProjectPermissions,
  makeSelectProjectReportPictureUploading,
  makeSelectSavingPinCategory,
  makeSelectUpdatingPinField
} from './selectors'
import Tabs from '../../components/tabs'
import settingsReducer from './settingsReducer'
import ProjectSettingsContextWrapper from './ProjectSettingsContext'
import {
  CLEAR_STATE,
  FLUSH_READY_TO_SAVE,
  moduleBaseHandler,
  SET_ACTIVE_MODULE,
  SET_FORM_ERROR,
  SET_READY_TO_SAVE,
  SET_SAVING_METHOD,
  UPDATE_FORM
} from './constants'
import Popup from '../../components/popup'

import { plusIcon } from './images'
import { BUTTON_TYPES } from '../../components/Button'
import CancelWarningPopUp from './components/CancelWarningPopUp'
import { buttonType } from '../../components/popup-new/constants'
import colorsOptions from '../../components/stories/fundamentals/colors-styles/constants'
import {
  textStyleSizeOptions,
  textStyleWeightOptions,
  textStyleSizeStyleOptions,
  textStyleWeightStyleOptions
} from '../../components/stories/fundamentals/text-styles/constants'
import { makeSelectFeatureInfo } from '../FeatureAvailability/selectors'
import { useFlag } from '@unleash/proxy-client-react'
import { unleashFlags } from '../../unleash-constants'

function ProjectSettings({
  projectPermissions = [],
  projectDetails,
  getFields,
  savingPinCategory,
  getPinCategories,
  updatingPinField,
  creatingPinField,
  intl: { formatMessage }
}) {
  const {
    path,
    params: { projectId }
  } = useRouteMatch()

  const reduxDispatch = useDispatch()

  const savingProjectDetails = useSelector(makeSelectSavingProjectDetails())
  const saveProjectDetailsError = useSelector(
    makeSelectSavingProjectDetailsError()
  )

  const location = useLocation()

  const reportsEnabled = useFlag(unleashFlags.REPORTS)

  const selectPinFieldsFeatureInfo = makeSelectFeatureInfo(
    'PIN_FIELDS',
    projectId
  )
  const { featureInfo: pinFieldFeatureInfo } = useSelector(
    selectPinFieldsFeatureInfo
  )

  const { push } = useHistory()

  const initialState = useMemo(
    () => ({
      activeTab: 'details',
      moduleInfo: moduleBaseHandler(reportsEnabled),
      moduleChanged: {},
      moduleErrors: {},
      readyToSave: {},
      consolidatedForm: {},
      loading: false,
      saving: false,
      projectId
    }),
    []
  )
  // use redux store for global data, and use context for local/component specific state
  const [state, dispatch] = useReducer(settingsReducer, initialState)
  const [projectName, setProjectName] = useState('')
  const [showCancelWarning, setShowCancelWarning] = useState(false)
  const [pendingClose, setPendingClose] = useState(false)

  useEffect(() => {
    if (pendingClose && !savingProjectDetails) {
      if (!saveProjectDetailsError) {
        closePopUp()
      }
      setPendingClose(false)
    }
  }, [pendingClose, savingProjectDetails, saveProjectDetailsError])

  // on unmount
  useEffect(
    () => () => {
      dispatch({ type: CLEAR_STATE, initialState })
    },
    []
  )
  useEffect(() => {
    if (projectDetails?.id === projectId) {
      setProjectName(projectDetails.name)
    } else {
      setProjectName('')
    }
  }, [projectDetails?.id, projectId])

  useEffect(() => {
    if (projectId && (!updatingPinField || !creatingPinField)) {
      getFields(projectId)
    }
  }, [projectId, updatingPinField, creatingPinField])

  useEffect(() => {
    if (!savingPinCategory) getPinCategories(projectId)
  }, [savingPinCategory])

  useEffect(() => {
    if (!savingPinCategory) getPinCategories(projectId)
  }, [savingPinCategory])
  const {
    activeTab,
    consolidatedForm,
    loading,
    saving,
    moduleInfo,
    readyToSave,
    moduleErrors
  } = state

  const closePopUp = useCallback(() => {
    reduxDispatch(actions.clearProjectDetailsError())
    const { search } = location ?? ''

    const finalPath =
      path.indexOf(':projectId/') > -1 ? `/project/${projectId}` : '/project'

    push(`${finalPath}${search}`)
  }, [])
  const stateMethods = {
    setActiveTab: (tabSlug) => {
      dispatch({ type: SET_ACTIVE_MODULE, value: tabSlug })
    },
    setReadyToSave: (module, value) => {
      dispatch({ type: SET_READY_TO_SAVE, module, value })
    },
    setSavingMethod: (module, value) => {
      dispatch({ type: SET_SAVING_METHOD, module, value })
    },
    updateForm: (module, keyValuePair, overwriteMode) => {
      dispatch({ type: UPDATE_FORM, module, keyValuePair, overwriteMode })
    },
    setFormError: (module, keyValuePair, overwriteMode = false) => {
      dispatch({ type: SET_FORM_ERROR, module, keyValuePair, overwriteMode })
    },
    formatMessage
  }
  const tabItems = Object.values(moduleInfo).map(
    ({ slug, title, featureSlug }) => {
      const hasError = Object.values(moduleErrors[slug] ?? {}).some((e) => !!e)
      const hasChanged = readyToSave[slug] === true
      let afterTitle = ''
      if (hasChanged) afterTitle = '*'
      if (hasError) afterTitle = ' (!)'
      return {
        slug,
        title: `${formatMessage({ id: title })}${afterTitle}`,
        translated: true,
        featureSlug,
        afterTitle,
        style: {
          ...(hasError && {
            color: colorsOptions.secondaryColors.red.strong
          }),
          ...((hasError || hasChanged) && {
            fontFamily:
              textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-600']]
                .fontFamily
          })
        }
      }
    }
  )
  const changeTab = useCallback((tab) => {
    stateMethods.setActiveTab(tab.slug)
  }, [])
  const saveActive =
    Object.values(state.readyToSave).some((mC) => mC === true) &&
    !Object.values(state.moduleErrors).some((mC) => Object.keys(mC).length)
  const save = () => {
    let groupedSave = {}
    let groupedSaveMethod
    Object.keys(state.readyToSave).forEach((module) => {
      if (state.readyToSave[module]) {
        if (moduleInfo[module].groupedSave) {
          groupedSave = {
            ...groupedSave,
            ...consolidatedForm[module]
          }
          groupedSaveMethod = moduleInfo[module].save
        } else {
          moduleInfo[module].save(consolidatedForm[module])
        }
      }
    })
    if (groupedSaveMethod) groupedSaveMethod(groupedSave)
    dispatch({ type: FLUSH_READY_TO_SAVE })
  }
  const leftPopUpButtons = [
    {
      onClick: () => {
        if (
          Object.values(consolidatedForm).length &&
          projectPermissions.includes('EditSettings')
        ) {
          setShowCancelWarning(true)
        } else {
          closePopUp()
        }
      },
      text: formatMessage({ id: 'cancel' }),
      styles: {
        background: 'transparent',
        borderColor: colorsOptions.greys['grey-100'],
        fontSize:
          textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']].fontSize
      }
    },
    {
      text: formatMessage({ id: 'add_new_field' }),
      hidden:
        activeTab !== 'design' ||
        !pinFieldFeatureInfo ||
        !pinFieldFeatureInfo.available,
      icon: plusIcon,
      className: 'addField',
      altText: formatMessage({ id: 'add_new_field' }),
      type: BUTTON_TYPES.dropdown,
      projectId,
      styles: {
        background: colorsOptions.docuToolsOrange.orange,
        borderColor: colorsOptions.docuToolsOrange.orange,
        color: colorsOptions.greys.white,
        fontSize:
          textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']].fontSize
      }
    }
  ]
  const rightPopupButtons = [
    {
      onClick: save,
      text: formatMessage({ id: 'save' }),
      disabled: !saveActive,
      hidden: !projectPermissions.includes('EditSettings'),
      styles: {
        background: 'transparent',
        borderColor: colorsOptions.greys['grey-100'],
        color: colorsOptions.greys['grey-100'],
        fontSize:
          textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']].fontSize
      }
    },
    {
      onClick: () => {
        save()
        setPendingClose(true)
      },
      text: formatMessage({ id: 'saveAndClose' }),
      disabled: !saveActive,
      hidden: !projectPermissions.includes('EditSettings'),
      styles: {
        background: 'transparent',
        borderColor: colorsOptions.docuToolsOrange.orange,
        color: colorsOptions.docuToolsOrange.orange,
        fontSize:
          textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']].fontSize
      }
    }
  ]
  const ActiveTab = moduleInfo[activeTab]?.Element
  const cancelWarningButtons = useMemo(
    () => [
      {
        onClick: () => {
          setShowCancelWarning(false)
        },
        text: formatMessage({ id: 'cancel' }),
        type: buttonType.BUTTON,
        width: '120px',
        $noPadding: true,
        fontSize:
          textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']].fontSize,
        styles: {
          borderColor: colorsOptions.greys['grey-100'],
          marginRight: '16px'
        }
      },
      {
        onClick: closePopUp,
        text: formatMessage({ id: 'close' }),
        isPrimary: true,
        type: buttonType.BUTTON,
        width: '120px',
        $noPadding: true,
        fontSize:
          textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']].fontSize,
        styles: { background: colorsOptions.secondaryColors.red.normal }
      }
    ],
    []
  )

  return (
    <Popup
      title={{ id: 'projectSettings_projectSettingsOf' }}
      values={{
        name: projectName,
        conjunction: (
          <span
            style={{
              fontFamily:
                textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-500']]
                  .fontFamily
            }}
          >
            {formatMessage({ id: 'of' })}
          </span>
        )
      }}
      closePopUp={closePopUp}
      showCloseTopRight
      leftButtons={leftPopUpButtons}
      rightButtons={rightPopupButtons}
      loading={saving || loading}
      dataTestId="ProjectSettings-Popup"
      noBodyWrapper
      size="MEDIUM"
    >
      <Tabs
        tabItems={tabItems}
        currentTab={activeTab}
        onChange={changeTab}
        singleChild
      >
        {showCancelWarning && (
          <CancelWarningPopUp
            formatMessage={formatMessage}
            rightPopupButtons={cancelWarningButtons}
            authorized={projectPermissions.includes('EditSettings')}
          />
        )}
        <ProjectSettingsContextWrapper state={state} methods={stateMethods}>
          {!!ActiveTab && <ActiveTab />}
        </ProjectSettingsContextWrapper>
      </Tabs>
    </Popup>
  )
}

ProjectSettings.propTypes = {
  projectPermissions: array,
  projectDetails: object,
  intl: object.isRequired,
  getFields: func,
  savingPinCategory: bool,
  getPinCategories: func,
  updatingPinField: bool,
  creatingPinField: bool
}

const mapStateToProps = createStructuredSelector({
  projectReportPictureUploading: makeSelectProjectReportPictureUploading(),
  projectPermissions: makeSelectProjectPermissions(),
  projectDetails: makeSelectProjectDetails(),
  savingPinCategory: makeSelectSavingPinCategory(),
  updatingPinField: makeSelectUpdatingPinField(),
  creatingPinField: makeSelectCreatingPinField()
})

function mapDispatchToProps(dispatch) {
  return {
    getFields: (...attributes) => dispatch(actions.getFields(...attributes)),
    getPinCategories: (...attributes) =>
      dispatch(actions.getPinCategories(...attributes))
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(ProjectSettings))
